import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  CircularProgress,
  Box,
  Typography,
  Chip,
  Card,
  Grid,
} from '@mui/material';
import Iconify from '../iconify';
import Scrollbar from '../scrollbar';
import { useUserDetails } from '../../hooks/useUserDetails';
import AddTicketsDialog from './AddTicketsDialog';

const TRANSACTION_TYPE_COLORS = {
  0: 'primary',
  1: 'success',
  2: 'info',
  3: 'secondary',
  4: 'warning',
  5: 'success',
  6: 'error'
};

const TRANSACTION_TYPE = {
  0: 'Adjustment',
  1: 'Daily Contest',
  2: 'Streak',
  3: 'Referral',
  4: 'Raffle',
  5: 'Win Raffle',
  6: 'Refund',
  7: 'Profile Completion Reward'
};

export default function TicketsTab({ userId }) {
  const { user, ticketTransactions, totalTransactions, fetchUserTicketTransactions, addTickets } = useUserDetails();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    loadTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      await fetchUserTicketTransactions(userId, {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });
    } catch (err) {
      console.error('Failed to load transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddTickets = async (userId, amount) => {
    await addTickets(userId, amount);
    await loadTransactions();
  };

  if (loading && ticketTransactions.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Card
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 2,
                background: 'linear-gradient(135deg, #8B5CF615 0%, #8B5CF605 100%)',
                border: '1px solid #8B5CF620',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 1.5, 
                  bgcolor: '#8B5CF620',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Iconify icon="eva:pricetags-fill" width={20} sx={{ color: '#8B5CF6' }} />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Ticket Balance
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ color: '#8B5CF6', fontWeight: 700 }}>
                {user?.ticket_balance || '-'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Current active tickets
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Card
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 2,
                background: 'linear-gradient(135deg, #10B98115 0%, #10B98105 100%)',
                border: '1px solid #10B98120',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 1.5, 
                  bgcolor: '#10B98120',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Iconify icon="eva:award-fill" width={20} sx={{ color: '#10B981' }} />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Total Tickets Earned
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ color: '#10B981', fontWeight: 700 }}>
                {user?.total_tickets || '-'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Lifetime total accumulated
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<Iconify icon="eva:plus-fill" />}
          onClick={() => setDialogOpen(true)}
        >
          Add Tickets
        </Button>
      </Box>

      {ticketTransactions.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <Typography variant="body2" color="text.secondary">
            No ticket transactions found
          </Typography>
        </Box>
      ) : (
        <>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>User ID</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>Timestamp</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ticketTransactions.map((transaction) => (
                    <TableRow key={transaction.transactionId} hover>
                      <TableCell>{transaction.transactionId}</TableCell>
                      <TableCell>{transaction.userId}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: transaction.amount >= 0 ? 'success.main' : 'error.main',
                            fontWeight: 600,
                          }}
                        >
                          {transaction.amount >= 0 ? '+' : ''}
                          {transaction.amount}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={TRANSACTION_TYPE[transaction.transactionType]}
                          size="small"
                          color={TRANSACTION_TYPE_COLORS[transaction.transactionType] || 'default'}
                        />
                      </TableCell>
                      <TableCell>{transaction.reference || '-'}</TableCell>
                      <TableCell>
                        {transaction.createdAt
                          ? new Date(transaction.createdAt).toLocaleString()
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalTransactions}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      <AddTicketsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        user={{ user_id: userId }}
        onConfirm={handleAddTickets}
      />
    </>
  );
}
